// Travel and expense claims paid through payroll runs. A claim belongs to one
// run at a time: a new run only picks up approved claims that no other run
// (draft, draft after an accounting annulment, approved or paid) still holds.
// Two drafts could otherwise both pay the same claim (app gap audit H9).
// Mirrors functions/payroll/payrollTravelClaims.ts; the server re-validates
// every claim when a run is approved.
(function initPayrollTravelClaims(global) {
  const safeArray = (value) => (Array.isArray(value) ? value : []);
  const text = (value) => String(value ?? '').trim();
  const POSTED_RUN_STATUSES = new Set(['approved', 'paid']);

  const runTravelIds = (run) => {
    const ids = new Set();
    safeArray(run?.lines).forEach((line) => {
      safeArray(line?.items).forEach((item) => {
        [item?.sourceId, item?.travelId, item?.sourceTravelId].forEach((id) => { if (text(id)) ids.add(text(id)); });
      });
      safeArray(line?.postedExpenseIds).forEach((id) => { if (text(id)) ids.add(text(id)); });
    });
    return ids;
  };

  // Claims held by a run that has not been reversed (deleted).
  const claimedTravelIds = (state, { excludeRunId = '' } = {}) => {
    const ids = new Set();
    safeArray(state?.payrollRuns).forEach((run) => {
      if (text(run?.status) === 'reversed' || (excludeRunId && text(run?.id) === text(excludeRunId))) return;
      runTravelIds(run).forEach((id) => ids.add(id));
    });
    return ids;
  };

  // Predicate for building a new run: approved and not held by another run.
  const readyForNewRun = (state) => {
    const claimed = claimedTravelIds(state);
    return (travel) => text(travel?.status) === 'approved' && !claimed.has(text(travel?.id));
  };

  // Claims on `run` that approval would reject. Same rules as the server.
  const payabilityIssues = (state, run) => {
    const runId = text(run?.id);
    const travels = new Map(safeArray(state?.employeePortalTravels).map((travel) => [text(travel?.id), travel]));
    const paidElsewhere = new Map();
    safeArray(state?.payrollRuns).forEach((other) => {
      if (text(other?.id) === runId || !POSTED_RUN_STATUSES.has(text(other?.status))) return;
      runTravelIds(other).forEach((id) => { if (!paidElsewhere.has(id)) paidElsewhere.set(id, text(other?.id)); });
    });
    const issues = [];
    runTravelIds(run).forEach((travelId) => {
      const travel = travels.get(travelId);
      if (!travel) return;
      const status = text(travel.status);
      const described = { travelId, status, employeeName: text(travel.employeeName), description: text(travel.purpose || travel.destination || travel.description) };
      if (paidElsewhere.has(travelId)) issues.push({ ...described, reason: 'paid-in-other-run', otherRunId: paidElsewhere.get(travelId) });
      else if (status === 'included-in-payroll') { if (text(travel.payrollRunId) !== runId) issues.push({ ...described, reason: 'already-included' }); }
      else if (status !== 'approved') issues.push({ ...described, reason: 'not-approved' });
    });
    return issues;
  };

  global.PlummePayrollTravelClaims = { runTravelIds, claimedTravelIds, readyForNewRun, payabilityIssues };
})(typeof window !== 'undefined' ? window : globalThis);
